import type { InjectionKey } from 'vue'
import type { RoleRepository } from '#/domain/role/RoleRepository'
import type { FeatureFlagRepository } from '#/domain/featureFlag/FeatureFlagRepository'
import type { CustomerAccessRepository } from '#/domain/customerAccess/CustomerAccessRepository'
import type { ConnectionRepository } from '#/domain/connection/ConnectionRepository'
import type { OrganizationRepository } from '#/domain/organization/OrganizationRepository'
import type { ApplicationRepository } from '#/domain/application/ApplicationRepository'
import type { InvitationRepository } from '#/domain/invitation/InvitationRepository'
import type { AssetsLinkRepository } from '#/domain/assetsLink/AssetsLinkRepository'
import type { AssetUploadGateway } from '#/domain/assetsLink/AssetUploadGateway'
import type { UserRepository } from '#/domain/user/UserRepository'
import type { CatalogRepository } from '#/domain/catalog/CatalogRepository'
import type { AuditLogRepository } from '#/domain/audit/AuditLogRepository'

import type { GetRoleListUseCase } from '#/application/role/GetRoleListUseCase'
import type { GetAllProductsFeatureFlagsUseCase } from '#/application/featureFlag/GetAllProductsFeatureFlagsUseCase'
import type { ToggleFeatureFlagUseCase } from '#/application/featureFlag/ToggleFeatureFlagUseCase'
import type { IsFeatureEnabledUseCase } from '#/application/featureFlag/IsFeatureEnabledUseCase'
import type { GetAccessibleCustomersUseCase } from '#/application/customerAccess/GetAccessibleCustomersUseCase'
import type { GetCustomerFeaturesUseCase } from '#/application/customerAccess/GetCustomerFeaturesUseCase'
import type { GetAllFeaturesUseCase } from '#/application/customerAccess/GetAllFeaturesUseCase'
import type { GrantCustomerFeatureUseCase } from '#/application/customerAccess/GrantCustomerFeatureUseCase'
import type { RevokeCustomerFeatureUseCase } from '#/application/customerAccess/RevokeCustomerFeatureUseCase'
import type { GetConnectionListUseCase } from '#/application/connection/GetConnectionListUseCase'
import type { CreateConnectionUseCase } from '#/application/connection/CreateConnectionUseCase'
import type { GetAuditLogUseCase } from '#/application/audit/GetAuditLogUseCase'
import type { GetOrganizationListUseCase } from '#/application/organization/GetOrganizationListUseCase'
import type { GetOrganizationUseCase } from '#/application/organization/GetOrganizationUseCase'
import type { FindAllOrganizationsUseCase } from '#/application/organization/FindAllOrganizationsUseCase'
import type { CreateOrganizationUseCase } from '#/application/organization/CreateOrganizationUseCase'
import type { UpdateOrganizationUseCase } from '#/application/organization/UpdateOrganizationUseCase'
import type { DeleteOrganizationUseCase } from '#/application/organization/DeleteOrganizationUseCase'
import type { GetApplicationListUseCase } from '#/application/application/GetApplicationListUseCase'
import type { GetInvitationListUseCase } from '#/application/invitation/GetInvitationListUseCase'
import type { CreateInvitationsUseCase } from '#/application/invitation/CreateInvitationsUseCase'
import type { ResendInvitationUseCase } from '#/application/invitation/ResendInvitationUseCase'
import type { GetAssetsLinkUseCase } from '#/application/assetsLink/GetAssetsLinkUseCase'
import type { CreateAssetsLinkUseCase } from '#/application/assetsLink/CreateAssetsLinkUseCase'
import type { UploadAssetUseCase } from '#/application/assetsLink/UploadAssetUseCase'
import type { GetUserUseCase } from '#/application/user/GetUserUseCase'
import type { GetUserListUseCase } from '#/application/user/GetUserListUseCase'
import type { GetUserLogsUseCase } from '#/application/user/GetUserLogsUseCase'
import type { GetUserApplicationsUseCase } from '#/application/user/GetUserApplicationsUseCase'
import type { GetUserRolesUseCase } from '#/application/user/GetUserRolesUseCase'
import type { AssignRolesUseCase } from '#/application/user/AssignRolesUseCase'
import type { RemoveRolesUseCase } from '#/application/user/RemoveRolesUseCase'
import type { RemoveMemberUseCase } from '#/application/user/RemoveMemberUseCase'
import type { UpdateUserMeUseCase } from '#/application/user/UpdateUserMeUseCase'
import type { GetCatalogMetricListUseCase } from '#/application/catalog/GetCatalogMetricListUseCase'
import type { GetCatalogLayerListUseCase } from '#/application/catalog/GetCatalogLayerListUseCase'
import type { GetCatalogApplicationListUseCase } from '#/application/catalog/GetCatalogApplicationListUseCase'
import type { GetCatalogMetricCategoryListUseCase } from '#/application/catalog/GetCatalogMetricCategoryListUseCase'
import type { GetAssignedMetricIdsUseCase } from '#/application/catalog/GetAssignedMetricIdsUseCase'
import type { BulkAssignMetricsToOrganizationUseCase } from '#/application/catalog/BulkAssignMetricsToOrganizationUseCase'
import type { ApplyFireriskMetricPresetUseCase } from '#/application/catalog/ApplyFireriskMetricPresetUseCase'
import type { UnassignMetricFromOrganizationUseCase } from '#/application/catalog/UnassignMetricFromOrganizationUseCase'
import type { GetMetricByOrgIdUseCase } from '#/application/catalog/GetMetricByOrgIdUseCase'
import type { GetLayerByOrgIdUseCase } from '#/application/catalog/GetLayerByOrgIdUseCase'
import type { GetLayerCategoriesByOrgIdUseCase } from '#/application/catalog/GetLayerCategoriesByOrgIdUseCase'
import type { AssignLayerUseCase } from '#/application/catalog/AssignLayerUseCase'
import type { UnassignLayerUseCase } from '#/application/catalog/UnassignLayerUseCase'

export const InjectionTypes = {
  RoleRepository: Symbol('RoleRepository') as InjectionKey<RoleRepository>,
  FeatureFlagRepository: Symbol('FeatureFlagRepository') as InjectionKey<FeatureFlagRepository>,
  CustomerAccessRepository: Symbol(
    'CustomerAccessRepository',
  ) as InjectionKey<CustomerAccessRepository>,
  ConnectionRepository: Symbol('ConnectionRepository') as InjectionKey<ConnectionRepository>,
  OrganizationRepository: Symbol('OrganizationRepository') as InjectionKey<OrganizationRepository>,
  ApplicationRepository: Symbol('ApplicationRepository') as InjectionKey<ApplicationRepository>,
  InvitationRepository: Symbol('InvitationRepository') as InjectionKey<InvitationRepository>,
  AssetsLinkRepository: Symbol('AssetsLinkRepository') as InjectionKey<AssetsLinkRepository>,
  AssetUploadGateway: Symbol('AssetUploadGateway') as InjectionKey<AssetUploadGateway>,
  AuditLogRepository: Symbol('AuditLogRepository') as InjectionKey<AuditLogRepository>,
  UserRepository: Symbol('UserRepository') as InjectionKey<UserRepository>,
  CatalogRepository: Symbol('CatalogRepository') as InjectionKey<CatalogRepository>,

  GetRoleListUseCase: Symbol('GetRoleListUseCase') as InjectionKey<GetRoleListUseCase>,
  GetAllProductsFeatureFlagsUseCase: Symbol(
    'GetAllProductsFeatureFlagsUseCase',
  ) as InjectionKey<GetAllProductsFeatureFlagsUseCase>,
  ToggleFeatureFlagUseCase: Symbol(
    'ToggleFeatureFlagUseCase',
  ) as InjectionKey<ToggleFeatureFlagUseCase>,
  IsFeatureEnabledUseCase: Symbol(
    'IsFeatureEnabledUseCase',
  ) as InjectionKey<IsFeatureEnabledUseCase>,
  GetAccessibleCustomersUseCase: Symbol(
    'GetAccessibleCustomersUseCase',
  ) as InjectionKey<GetAccessibleCustomersUseCase>,
  GetCustomerFeaturesUseCase: Symbol(
    'GetCustomerFeaturesUseCase',
  ) as InjectionKey<GetCustomerFeaturesUseCase>,
  GetAllFeaturesUseCase: Symbol('GetAllFeaturesUseCase') as InjectionKey<GetAllFeaturesUseCase>,
  GrantCustomerFeatureUseCase: Symbol(
    'GrantCustomerFeatureUseCase',
  ) as InjectionKey<GrantCustomerFeatureUseCase>,
  RevokeCustomerFeatureUseCase: Symbol(
    'RevokeCustomerFeatureUseCase',
  ) as InjectionKey<RevokeCustomerFeatureUseCase>,
  GetConnectionListUseCase: Symbol(
    'GetConnectionListUseCase',
  ) as InjectionKey<GetConnectionListUseCase>,
  CreateConnectionUseCase: Symbol(
    'CreateConnectionUseCase',
  ) as InjectionKey<CreateConnectionUseCase>,
  GetAuditLogUseCase: Symbol('GetAuditLogUseCase') as InjectionKey<GetAuditLogUseCase>,
  GetOrganizationListUseCase: Symbol(
    'GetOrganizationListUseCase',
  ) as InjectionKey<GetOrganizationListUseCase>,
  GetOrganizationUseCase: Symbol('GetOrganizationUseCase') as InjectionKey<GetOrganizationUseCase>,
  FindAllOrganizationsUseCase: Symbol(
    'FindAllOrganizationsUseCase',
  ) as InjectionKey<FindAllOrganizationsUseCase>,
  CreateOrganizationUseCase: Symbol(
    'CreateOrganizationUseCase',
  ) as InjectionKey<CreateOrganizationUseCase>,
  UpdateOrganizationUseCase: Symbol(
    'UpdateOrganizationUseCase',
  ) as InjectionKey<UpdateOrganizationUseCase>,
  DeleteOrganizationUseCase: Symbol(
    'DeleteOrganizationUseCase',
  ) as InjectionKey<DeleteOrganizationUseCase>,
  GetApplicationListUseCase: Symbol(
    'GetApplicationListUseCase',
  ) as InjectionKey<GetApplicationListUseCase>,
  GetInvitationListUseCase: Symbol(
    'GetInvitationListUseCase',
  ) as InjectionKey<GetInvitationListUseCase>,
  CreateInvitationsUseCase: Symbol(
    'CreateInvitationsUseCase',
  ) as InjectionKey<CreateInvitationsUseCase>,
  ResendInvitationUseCase: Symbol(
    'ResendInvitationUseCase',
  ) as InjectionKey<ResendInvitationUseCase>,
  GetAssetsLinkUseCase: Symbol('GetAssetsLinkUseCase') as InjectionKey<GetAssetsLinkUseCase>,
  CreateAssetsLinkUseCase: Symbol(
    'CreateAssetsLinkUseCase',
  ) as InjectionKey<CreateAssetsLinkUseCase>,
  UploadAssetUseCase: Symbol('UploadAssetUseCase') as InjectionKey<UploadAssetUseCase>,
  GetUserUseCase: Symbol('GetUserUseCase') as InjectionKey<GetUserUseCase>,
  GetUserListUseCase: Symbol('GetUserListUseCase') as InjectionKey<GetUserListUseCase>,
  GetUserLogsUseCase: Symbol('GetUserLogsUseCase') as InjectionKey<GetUserLogsUseCase>,
  GetUserApplicationsUseCase: Symbol(
    'GetUserApplicationsUseCase',
  ) as InjectionKey<GetUserApplicationsUseCase>,
  GetUserRolesUseCase: Symbol('GetUserRolesUseCase') as InjectionKey<GetUserRolesUseCase>,
  AssignRolesUseCase: Symbol('AssignRolesUseCase') as InjectionKey<AssignRolesUseCase>,
  RemoveRolesUseCase: Symbol('RemoveRolesUseCase') as InjectionKey<RemoveRolesUseCase>,
  RemoveMemberUseCase: Symbol('RemoveMemberUseCase') as InjectionKey<RemoveMemberUseCase>,
  UpdateUserMeUseCase: Symbol('UpdateUserMeUseCase') as InjectionKey<UpdateUserMeUseCase>,
  GetCatalogMetricListUseCase: Symbol(
    'GetCatalogMetricListUseCase',
  ) as InjectionKey<GetCatalogMetricListUseCase>,
  GetCatalogLayerListUseCase: Symbol(
    'GetCatalogLayerListUseCase',
  ) as InjectionKey<GetCatalogLayerListUseCase>,
  GetCatalogApplicationListUseCase: Symbol(
    'GetCatalogApplicationListUseCase',
  ) as InjectionKey<GetCatalogApplicationListUseCase>,
  GetCatalogMetricCategoryListUseCase: Symbol(
    'GetCatalogMetricCategoryListUseCase',
  ) as InjectionKey<GetCatalogMetricCategoryListUseCase>,
  GetAssignedMetricIdsUseCase: Symbol(
    'GetAssignedMetricIdsUseCase',
  ) as InjectionKey<GetAssignedMetricIdsUseCase>,
  BulkAssignMetricsToOrganizationUseCase: Symbol(
    'BulkAssignMetricsToOrganizationUseCase',
  ) as InjectionKey<BulkAssignMetricsToOrganizationUseCase>,
  ApplyFireriskMetricPresetUseCase: Symbol(
    'ApplyFireriskMetricPresetUseCase',
  ) as InjectionKey<ApplyFireriskMetricPresetUseCase>,
  UnassignMetricFromOrganizationUseCase: Symbol(
    'UnassignMetricFromOrganizationUseCase',
  ) as InjectionKey<UnassignMetricFromOrganizationUseCase>,
  GetMetricByOrgIdUseCase: Symbol(
    'GetMetricByOrgIdUseCase',
  ) as InjectionKey<GetMetricByOrgIdUseCase>,
  GetLayerByOrgIdUseCase: Symbol('GetLayerByOrgIdUseCase') as InjectionKey<GetLayerByOrgIdUseCase>,
  GetLayerCategoriesByOrgIdUseCase: Symbol(
    'GetLayerCategoriesByOrgIdUseCase',
  ) as InjectionKey<GetLayerCategoriesByOrgIdUseCase>,
  AssignLayerUseCase: Symbol('AssignLayerUseCase') as InjectionKey<AssignLayerUseCase>,
  UnassignLayerUseCase: Symbol('UnassignLayerUseCase') as InjectionKey<UnassignLayerUseCase>,
} as const
